import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const logoUrl = 'https://customer-assets-eiarnc6j.emergentagent.net/job_ot-partners/artifacts/sglfjlo8_OT_Resilience_LOGO-removebg-preview.png';

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrolly > 50;
      setIsScrolled(prev => prev !== scrolled ? scrolled : prev);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', type: 'scroll', target: '#services' },
    { label: 'About', type: 'route', target: '/about' },
    { label: 'Contact', type: 'route', target: '/contact' }
  ];

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.type === 'route') {
      navigate(link.target);
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else if (link.type === 'scroll') {
      if (location.pathname !== '/') {
        navigate('/' + link.target);
      } else {
        document.querySelector(link.target)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleGetStarted = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-slate-950/90 backdrop-blur-xl border-b border-white/10 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
        {/* Logo */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="relative z-50"
        >
          <Link to="/" data-testid="header-logo-link" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
            <img 
              src={logoUrl} 
              alt="OT Resilience" 
              className="h-28 w-auto"
              style={{ 
                objectFit: 'cover', 
                objectPosition: 'center top',
                clipPath: 'inset(0 0 38% 0)'
              }}
            />
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link, i) => (
            <motion.button
              key={link.label}
              onClick={() => handleNavClick(link)}
              data-testid={`nav-${link.label.toLowerCase()}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, duration: 0.5 }}
              className="text-white/80 hover:text-amber-400 font-medium transition-colors duration-300 relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-400 group-hover:w-full transition-all duration-300" />
            </motion.button>
          ))}
          
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleGetStarted}
            data-testid="header-get-started-btn"
            className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300"
          >
            Get Started
          </motion.button>
        </div>

        {/* Mobile Menu Button */}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden relative z-50 w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg border border-white/20"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </motion.button>

        {/* Mobile Menu */}
        <motion.div
          initial={{ opacity: 0, x: '100%' }}
          animate={{
            opacity: isMobileMenuOpen ? 1 : 0,
            x: isMobileMenuOpen ? 0 : '100%'
          }}
          transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
          className="md:hidden fixed inset-0 bg-slate-950 pt-24 px-6 z-40"
        >
          <div className="flex flex-col gap-6">
            {navLinks.map((link, i) => (
              <motion.button
                key={link.label}
                initial={{ opacity: 0, x: 50 }}
                animate={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  x: isMobileMenuOpen ? 0 : 50
                }}
                transition={{ delay: 0.1 * i, duration: 0.3 }}
                onClick={() => handleNavClick(link)}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className="text-2xl text-white hover:text-amber-400 font-semibold text-left transition-colors duration-300"
              >
                {link.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, x: 50 }}
              animate={{
                opacity: isMobileMenuOpen ? 1 : 0,
                x: isMobileMenuOpen ? 0 : 50
              }}
              transition={{ delay: 0.3, duration: 0.3 }}
              onClick={handleGetStarted}
              data-testid="mobile-get-started-btn"
              className="mt-4 px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-semibold rounded-full text-xl"
            >
              Get Started
            </motion.button>
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};
